import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import { Container } from 'components/Containers';
import useOnlineOrderingLocationInformation from 'hooks/useOnlineOrderingLocationInformation';
import {
  StandardMenuHeroRestaurantName,
  StandardMenuHeroAddress,
  StandardMenuHeroPhoneNumber,
  detailsSx,
  ownTextState,
  ownIconState
} from 'bento-ordering/menus/components/StandardMenuHeroLocationInformationBlocks';
import { useIsMobile } from 'styles/hooks/mediaQueries';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import { OPACITY_20 } from 'constants/opacity';
import { calculateRgbaWithAlpha } from 'helpers/colorFormatter';

const StyledContainer = styled(Container)(({ theme }) => ({
  '&.MuiContainer-root': {
    position: 'relative',
    display: 'flex',
    borderBottom: `1px solid ${calculateRgbaWithAlpha(
      theme.palette.sectionNav.link.main,
      OPACITY_20
    )}`,
    padding: '0 16px'
  }
}));

export function StandardMobileMenuHeroLocationInformation({
  locationName,
  street,
  city,
  state,
  postalCode,
  phoneNumber
}) {
  return (
    <StyledContainer>
      <Box>
        <StandardMenuHeroRestaurantName
          locationName={locationName}
          ownState={ownTextState(() => ({
            fontSize: '16px',
            fontWeight: 500,
            marginTop: '20px',
            marginBottom: '5px'
          }))}
        />
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'row wrap',
            paddingBottom: '20px'
          }}
        >
          <StandardMenuHeroAddress
            street={street}
            city={city}
            state={state}
            postalCode={postalCode}
            IconProps={{
              ownState: ownIconState()
            }}
            DetailProps={{
              ownState: ownTextState(() => ({
                fontSize: '14px',
                lineHeight: '18px'
              }))
            }}
          />
          <StandardMenuHeroPhoneNumber
            phoneNumber={phoneNumber}
            IconProps={{
              ownState: ownIconState()
            }}
            LabelProps={{
              variant: ITEM_DESCRIPTION_AND_PRICES,
              ownState: ownTextState(theme => ({
                fontSize: '14px',
                lineHeight: '18px',
                '&:hover': {
                  color:
                    theme.palette.sectionNav.link.hover ??
                    theme.palette.mediumSoy
                }
              }))
            }}
          />
        </Box>
      </Box>
    </StyledContainer>
  );
}

StandardMobileMenuHeroLocationInformation.propTypes = {
  locationName: PropTypes.string.isRequired,
  street: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  postalCode: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired
};

function StandardMenuHeroLocationInformation() {
  const isMobile = useIsMobile();

  const locationInformation = useOnlineOrderingLocationInformation();
  if (!locationInformation) {
    return null;
  }
  const {
    locationName,
    street,
    city,
    state,
    postalCode,
    phoneNumber
  } = locationInformation;

  if (isMobile) {
    return (
      <StandardMobileMenuHeroLocationInformation {...locationInformation} />
    );
  }

  return (
    <Box
      sx={theme => ({
        position: 'absolute',
        top: '143px',
        zIndex: 1,
        [theme.breakpoints.between('sm', 'lg')]: {
          left: '20px'
        },
        [theme.breakpoints.up('lg')]: {
          top: '173px'
        }
      })}
    >
      <StandardMenuHeroRestaurantName locationName={locationName} />
      <StandardMenuHeroAddress
        street={street}
        city={city}
        state={state}
        postalCode={postalCode}
      />
      <StandardMenuHeroPhoneNumber
        phoneNumber={phoneNumber}
        LabelProps={{
          variant: ITEM_DESCRIPTION_AND_PRICES,
          ownState: ownTextState(theme => ({
            ...detailsSx,
            color: theme.palette.white
          }))
        }}
      />
    </Box>
  );
}

export default StandardMenuHeroLocationInformation;
